import type { Hotspot } from "../manifest/schema.js";
import { clampView, dragBy, imageLayout, isFound, panBy, zoomBy, type View } from "../telescope/view.js";
import { h } from "./dom.js";

type TelescopeHotspot = Extract<Hotspot, { kind: "telescope" }>;

export interface TelescopeDeps {
  mediaUrl: (rel: string) => string;
  announce: (msg: string) => void;
  /** Called once per opening when the target is centred. */
  onFound: (hs: TelescopeHotspot) => void;
  /** "Go to the shop" from the found message. */
  goToShop: (shopId: string) => void;
  shopName: (shopId: string) => string;
  close: () => void;
  reducedMotion: boolean;
}

/**
 * Interactive telescope: a round eyepiece over a wide panorama. Drag (mouse / touch), arrow keys or the on-screen
 * buttons pan; wheel, pinch, +/- zoom. The view is pure maths in `telescope/view.ts`; this file only renders it.
 */
export function telescopeContent(hs: TelescopeHotspot, d: TelescopeDeps): HTMLElement {
  let view: View = clampView({ cx: hs.start.x / 100, cy: hs.start.y / 100, zoom: 1 });
  let found = false;
  const img = h("img", { class: "scope-img", src: `${d.mediaUrl(hs.panorama)}.jpg`, alt: "", draggable: "false" });
  const eyepiece = h(
    "div",
    {
      class: `scope-eyepiece${d.reducedMotion ? " is-reduced" : ""}`,
      role: "application",
      tabindex: 0,
      "aria-roledescription": "telescope view",
      "aria-label": `${hs.alt} Use the arrow keys to look around, plus and minus to zoom.`,
      "data-testid": "telescope-view",
      "data-autofocus": true
    },
    img,
    h("span", { class: "scope-reticle", "aria-hidden": "true" })
  );
  const status = h("p", { class: "sr-only", role: "status", "aria-live": "polite" });
  const toast = h("div", { class: "scope-found", role: "status", hidden: true, "data-testid": "telescope-found" });

  const diameter = () => eyepiece.clientWidth || 320;
  const describe = () => {
    const horiz = view.cx < 0.4 ? "left" : view.cx > 0.6 ? "right" : "centre";
    const vert = view.cy < 0.4 ? "sky" : view.cy > 0.6 ? "rooftops" : "skyline";
    return `Looking ${horiz}, at the ${vert}, zoom ${view.zoom.toFixed(1)} times.`;
  };
  const render = () => {
    const l = imageLayout(view, diameter());
    img.style.width = `${l.width}px`;
    img.style.height = `${l.height}px`;
    img.style.transform = `translate(${l.left}px, ${l.top}px)`;
    eyepiece.dataset.cx = view.cx.toFixed(3);
    eyepiece.dataset.cy = view.cy.toFixed(3);
    eyepiece.dataset.zoom = view.zoom.toFixed(2);
    if (!found && isFound(view, hs.target)) {
      found = true;
      eyepiece.dataset.found = "true";
      const shop = d.shopName(hs.found.shopId);
      toast.append(
        h("strong", {}, "You found it! "),
        h("span", {}, hs.found.text),
        h("span", { class: "scope-code", "data-testid": "telescope-code" }, hs.found.code),
        h("button", { class: "btn", "data-testid": "telescope-go-shop", onclick: () => d.goToShop(hs.found.shopId) }, `Go to ${shop}`)
      );
      toast.hidden = false;
      d.announce(`You found it! ${hs.found.text} Code ${hs.found.code.split("").join(" ")}.`);
      d.onFound(hs);
    }
  };
  const set = (v: View, say = false) => {
    view = v;
    render();
    if (say) status.textContent = describe();
  };

  eyepiece.addEventListener("keydown", (e) => {
    const k = e.key;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (moves[k]) set(panBy(view, ...moves[k]!), true);
    else if (k === "+" || k === "=") set(zoomBy(view, 1.25), true);
    else if (k === "-" || k === "_") set(zoomBy(view, 0.8), true);
    else return;
    e.preventDefault();
  });

  // Drag with one pointer, pinch with two.
  const pointers = new Map<number, { x: number; y: number }>();
  let pinch = 0;
  eyepiece.addEventListener("pointerdown", (e) => {
    eyepiece.setPointerCapture?.(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    eyepiece.classList.add("is-dragging");
  });
  eyepiece.addEventListener("pointermove", (e) => {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      if (pinch) set(zoomBy(view, dist / pinch));
      pinch = dist;
    } else set(dragBy(view, e.clientX - prev.x, e.clientY - prev.y, diameter()));
  });
  const up = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    pinch = 0;
    if (pointers.size === 0) {
      eyepiece.classList.remove("is-dragging");
      status.textContent = describe();
    }
  };
  eyepiece.addEventListener("pointerup", up);
  eyepiece.addEventListener("pointercancel", up);
  eyepiece.addEventListener("wheel", (e) => {
    e.preventDefault();
    set(zoomBy(view, e.deltaY < 0 ? 1.1 : 1 / 1.1));
  }, { passive: false });

  const btn = (label: string, text: string, fn: () => void, testid: string) =>
    h("button", { class: "btn scope-btn", "aria-label": label, "data-testid": testid, onclick: fn }, text);
  const controls = h(
    "div",
    { class: "scope-controls", role: "group", "aria-label": "Telescope controls" },
    btn("Look left", "←", () => set(panBy(view, -1, 0), true), "scope-left"),
    btn("Look up", "↑", () => set(panBy(view, 0, -1), true), "scope-up"),
    btn("Look down", "↓", () => set(panBy(view, 0, 1), true), "scope-down"),
    btn("Look right", "→", () => set(panBy(view, 1, 0), true), "scope-right"),
    btn("Zoom in", "+", () => set(zoomBy(view, 1.25), true), "scope-zoom-in"),
    btn("Zoom out", "−", () => set(zoomBy(view, 0.8), true), "scope-zoom-out"),
    h("button", { class: "btn btn-primary scope-exit", "data-testid": "telescope-exit", onclick: d.close }, "Exit telescope")
  );

  const root = h(
    "div",
    { class: "scope" },
    h("h2", { class: "scope-title" }, hs.title),
    h("p", { class: "muted small scope-hint" }, `Drag to look around${matchMedia?.("(pointer: coarse)").matches ? ", pinch to zoom" : ", scroll to zoom"}. Arrow keys work too.`),
    eyepiece,
    toast,
    controls,
    status
  );
  // Lay out once the panel is in the document, and again when the eyepiece changes size.
  requestAnimationFrame(render);
  if (typeof ResizeObserver !== "undefined") new ResizeObserver(() => render()).observe(eyepiece);
  return root;
}
